import React, { createContext, PropsWithChildren, useContext } from 'react';
import { useImmer } from 'use-immer';
import { Driver, ExpeditionDetailHandlerView, LocationTimeslots } from './types';
import { EditCarInfoInDTO, EditTimeslotCarrierInDTO, ShipmentProgressFormDataInDTO } from '@api/ringil-api.v2';
import { Dayjs } from 'dayjs';
import {
  apiPragueFromDayjsUtc,
  apiUtcFromDayjs,
  applyTimeToUtcDayjsInTz,
  changeDatePreserveTimeInTz,
  dayjsFromApiUtc,
} from '@utils/dateWithTz.tsx';
import { createTimeslotNowInTz } from '@utils/timeslotDateTime.tsx';
import { ApiContext } from '@api/api';

export interface ExpeditionDetailHandlerContextType {
  state: ExpeditionDetailHandlerView;
  loadShipmentProgress: (companyId: string, applicationId: string) => void;
  selectSlot: (locationId: string, slotId: string) => void;
  changePlate: (plate: string) => void;
  changeDriver: (driver: Driver) => void;
  changeDispatcher: (dispatcherId: string) => void;
  createJobProgressTabForReadyToShipment: (companyId: string, applicationId: string, body: ShipmentProgressFormDataInDTO) => void;
  confirmAction: (locationId: string, when: Dayjs, timezoneId: string, propertyName: string) => void;
  loadTabActivePlus: () => void;
  finishShipment: () => void;
  loadShipmentPlan: () => void;
  changeCarrierSlots: (slots: EditTimeslotCarrierInDTO) => void;
  changeCarInfo: (data: EditCarInfoInDTO) => void;
  addCarrierSlot: (locationId: string) => void;
  changeSince: (locationId: string, since: string) => void;
  changeTill: (locationId: string, since: string) => void;
  changeCustomSlotDate: (locationId: string, date: Dayjs) => void;
  reconfirm: (companyId: string, applicationId: string, onSuccess: () => void) => void;
}

export const ExpeditionDetailHandlerContext = createContext<ExpeditionDetailHandlerContextType>(undefined);

interface ComponentProps {
  initialData: ExpeditionDetailHandlerView;
}

const ExpeditionDetailHandlerProvider: React.FC<PropsWithChildren<ComponentProps>> = ({ initialData, children }) => {
  const [state, setState] = useImmer<ExpeditionDetailHandlerView>(initialData);
  const {
    caGetShipmentDetailHeader,
    caGetShipmentDetailProgressTabForReadyToShipment,
    caGetShipmentDetailProgressTabAssigned,
    caSendAssignedShipmentForm,
    caFinishLocationWithData,
    caFinishShipment,
    caGetDetailShipmentPlan,
    caUpdateEditTimeslots,
    caEditCarInfo,
    caReconfirmedExpedition,
  } = useContext(ApiContext);

  const methods: ExpeditionDetailHandlerContextType = {
    state,
    loadShipmentProgress: (companyId: string, applicationId: string) => {
      caGetShipmentDetailProgressTabAssigned(companyId, applicationId, data => {
        setState(draft => {
          draft.assigningStateData = data;
          draft.createReadyToShipment = {
            carPlate: data.carPlate,
            dispatcherId: data.dispatcherId,
            driver: data.driverId ? { id: data.driverId, name: '' } : null,
            locationTimeslotIds: data.locations.reduce((array, value) => {
              return [
                ...array,
                {
                  locationId: value.id,
                  newSlot: null,
                  selectedSlotId: null,
                  customerSlots: value.timeslots,
                  timezoneId: value.timezoneId,
                },
              ];
            }, [] as LocationTimeslots[]),
          };
        });
      });
    },
    selectSlot: (locationId: string, timeslotId: string) => {
      setState(draft => {
        const location = draft.createReadyToShipment.locationTimeslotIds.find(l => l.locationId === locationId);
        /*        if (isNullOrUndefined(location)) {
                  draft.createReadyToShipment.locationTimeslotIds.push({ locationId, timeslotId });
                } else {
                  location.timeslotId = timeslotId;
                  location.newTimeslot = null;
                }*/
        location.timeslotId = timeslotId;
        location.newTimeslot = null;
      });
    },
    changePlate: (plate: string) => {
      setState(draft => {
        draft.createReadyToShipment.carPlate = plate;
      });
    },
    changeDriver: (driver: Driver) => {
      setState(draft => {
        draft.createReadyToShipment.driver = driver;
      });
    },
    changeDispatcher: (dispatcherId: string) => {
      setState(draft => {
        draft.createReadyToShipment.dispatcherId = dispatcherId;
      });
    },
    createJobProgressTabForReadyToShipment: (companyId: string, applicationId: string, body: ShipmentProgressFormDataInDTO) => {
      caSendAssignedShipmentForm(companyId, applicationId, body, () => {
        caGetShipmentDetailHeader(companyId, applicationId, data => {
          setState(draft => {
            draft.header = data;
          });
        });
      });
    },
    confirmAction: (locationId: string, when: Dayjs, timezoneId: string, propertyName: string) => {
      caFinishLocationWithData(
        state.header.companyId,
        state.header.applicationId,
        locationId,
        {
          [propertyName]: {
            finishedAt: apiPragueFromDayjsUtc(when, timezoneId),
            finishedAtUtc: apiUtcFromDayjs(when),
            isFinished: true,
          },
        },
        () => {
          caGetShipmentDetailProgressTabForReadyToShipment(state.header.companyId, state.header.applicationId, data => {
            setState(draft => {
              draft.header = data.expeditionHeaderPreview;
              draft.activeStateData = data;
            });
          });
        },
      );
    },
    loadTabActivePlus: () => {
      caGetShipmentDetailProgressTabForReadyToShipment(state.header.companyId, state.header.applicationId, data => {
        setState(draft => {
          draft.header = data.expeditionHeaderPreview;
          draft.activeStateData = data;
        });
      });
    },
    finishShipment: () => {
      caFinishShipment(state.header.companyId, state.header.applicationId, () => {
        caGetShipmentDetailProgressTabForReadyToShipment(state.header.companyId, state.header.applicationId, data => {
          setState(draft => {
            draft.header = data.expeditionHeaderPreview;
            draft.activeStateData = data;
          });
        });
      });
    },
    loadShipmentPlan: () => {
      caGetDetailShipmentPlan(state.header.companyId, state.header.applicationId, data => {
        setState(draft => {
          draft.header = data.expeditionHeaderPreview;
          draft.tabExpeditionPlan = data;
        });
      });
    },
    changeCarrierSlots: (slots: EditTimeslotCarrierInDTO) => {
      caUpdateEditTimeslots(state.header.companyId, state.header.applicationId, slots, () => {
        caGetShipmentDetailProgressTabForReadyToShipment(state.header.companyId, state.header.applicationId, data => {
          setState(draft => {
            draft.header = data.expeditionHeaderPreview;
            draft.activeStateData = data;
          });
        });
      });
    },
    changeCarInfo: (data: EditCarInfoInDTO) => {
      caEditCarInfo(state.header.companyId, state.header.applicationId, data, () => {
        caGetShipmentDetailProgressTabForReadyToShipment(state.header.companyId, state.header.applicationId, data => {
          setState(draft => {
            draft.header = data.expeditionHeaderPreview;
            draft.activeStateData = data;
          });
        });
      });
    },
    addCarrierSlot: (locationId: string) => {
      setState(draft => {
        const location = draft.createReadyToShipment.locationTimeslotIds.find(t => t.locationId === locationId);
        const timezoneId = location.timezoneId ?? 'Europe/Prague';

        // Get current time in location timezone, then convert to UTC for storage
        const { sinceUtc, tillUtc } = createTimeslotNowInTz(timezoneId, 30);

        location.newTimeslot = {
          // Legacy fields (deprecated but still sent for backward compatibility)
          timeslotSince: apiPragueFromDayjsUtc(sinceUtc, timezoneId),
          timeslotTill: apiPragueFromDayjsUtc(tillUtc, timezoneId),
          // New fields for proper timezone handling
          utcStart: apiUtcFromDayjs(sinceUtc),
          utcEnd: apiUtcFromDayjs(tillUtc),
          timezoneId: timezoneId,
        };
        location.timeslotId = null;
      });
    },
    changeSince: (locationId: string, since: string) => {
      setState(draft => {
        const location = draft.createReadyToShipment.locationTimeslotIds.find(t => t.locationId === locationId);
        const timezoneId = location.timezoneId ?? 'Europe/Prague';

        // Parse current UTC with fallback to legacy field
        const currentUtc = dayjsFromApiUtc(location.newTimeslot.utcStart ?? location.newTimeslot.timeslotSince);

        // Apply new time through timezone conversion
        const updatedUtc = applyTimeToUtcDayjsInTz(currentUtc, since, timezoneId);

        // Update both legacy and new fields
        location.newTimeslot.timeslotSince = apiPragueFromDayjsUtc(updatedUtc, timezoneId);
        location.newTimeslot.utcStart = apiUtcFromDayjs(updatedUtc);
      });
    },
    changeTill: (locationId: string, till: string) => {
      setState(draft => {
        const location = draft.createReadyToShipment.locationTimeslotIds.find(t => t.locationId === locationId);
        const timezoneId = location.timezoneId ?? 'Europe/Prague';

        // Parse current UTC with fallback to legacy field
        const currentUtc = dayjsFromApiUtc(location.newTimeslot.utcEnd ?? location.newTimeslot.timeslotTill);

        // Apply new time through timezone conversion
        const updatedUtc = applyTimeToUtcDayjsInTz(currentUtc, till, timezoneId);

        // Update both legacy and new fields
        location.newTimeslot.timeslotTill = apiPragueFromDayjsUtc(updatedUtc, timezoneId);
        location.newTimeslot.utcEnd = apiUtcFromDayjs(updatedUtc);
      });
    },
    changeCustomSlotDate: (locationId: string, date: Dayjs) => {
      setState(draft => {
        const location = draft.createReadyToShipment.locationTimeslotIds.find(t => t.locationId === locationId);
        const timezoneId = location.timezoneId ?? 'Europe/Prague';

        // Parse current UTC times with fallback to legacy fields
        const currentSinceUtc = dayjsFromApiUtc(location.newTimeslot.utcStart ?? location.newTimeslot.timeslotSince);
        const currentTillUtc = dayjsFromApiUtc(location.newTimeslot.utcEnd ?? location.newTimeslot.timeslotTill);

        // Use helper to preserve time when changing date
        const newSinceUtc = changeDatePreserveTimeInTz(currentSinceUtc, date, timezoneId);
        const newTillUtc = changeDatePreserveTimeInTz(currentTillUtc, date, timezoneId);

        // Update both legacy and new fields
        location.newTimeslot.timeslotSince = apiPragueFromDayjsUtc(newSinceUtc , timezoneId);
        location.newTimeslot.timeslotTill = apiPragueFromDayjsUtc(newTillUtc, timezoneId);
        location.newTimeslot.utcStart = apiUtcFromDayjs(newSinceUtc);
        location.newTimeslot.utcEnd = apiUtcFromDayjs(newTillUtc);
      });
    },
    reconfirm: (companyId: string, applicationId: string, onSuccess: () => void = () => {}) => {
      caReconfirmedExpedition(companyId, applicationId, () => {
        onSuccess();
      });
    },
  };

  return <ExpeditionDetailHandlerContext.Provider value={methods}>{children}</ExpeditionDetailHandlerContext.Provider>;
};

export default ExpeditionDetailHandlerProvider;
